import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Form, Spinner } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { shiftPreferenceAPI } from '../../services';
import './ShiftPreferenceWeeklyGrid.css';

/**
 * #230650 (đặc tả #191801, lệnh sếp 30/09/2026) — tab "Lưới tuần" của màn Ca ưu tiên.
 *
 * CHỈ ĐỌC: không có nút ghi nào. Số trong ô, mức màu, tóm tắt và "lấp được K ô đang thiếu" đều
 * do máy chủ tính (`GET /shift-preferences/admin/weekly-grid`) — màn này chỉ vẽ, không tự đếm lại,
 * để app và máy chủ không bao giờ nói lệch nhau. Duyệt ứng viên đi đúng nút Duyệt sẵn có ở
 * `/employees` (nút "Mở hồ sơ"), không đẻ luồng duyệt thứ hai.
 */

const LEVEL_CLASS = {
  none: 'spwg-none',
  just: 'spwg-just',
  plus1: 'spwg-plus1',
  plus2: 'spwg-plus2',
};

const pad2 = (n) => String(n).padStart(2, '0');

export const formatTemplateTime = (t) => {
  if (!t) return '';
  const start = (t.start_hour || 0) * 60 + (t.start_minute || 0);
  const end = (start + (t.duration_minutes || 0)) % (24 * 60);
  const fmt = (m) => `${pad2(Math.floor(m / 60))}:${pad2(m % 60)}`;
  return `${fmt(start)}–${fmt(end)}`;
};

/** Nhãn ngắn trong chip: "T3 19h" — lấy giờ bắt đầu ca, không dùng tên ca (tên có thể dài). */
const shortCellLabel = (dayLabel, t) =>
  `${dayLabel} ${t ? `${t.start_hour}h${t.start_minute ? pad2(t.start_minute) : ''}` : ''}`;

/** Giá trị `?o=` = `<templateId>-<thứ>`; templateId là uuid có dấu gạch nên tách ở gạch CUỐI. */
export const parseCellParam = (o) => {
  if (!o) return null;
  const i = o.lastIndexOf('-');
  if (i <= 0) return null;
  const day = Number(o.slice(i + 1));
  if (!Number.isInteger(day) || day < 0 || day > 6) return null;
  return { templateId: o.slice(0, i), day };
};

/** Link "Mở hồ sơ" của ứng viên — nút duyệt sẵn có ở `/employees`, không đẻ luồng duyệt mới. */
const hoSoLink = (branchId, a) =>
  `/employees?branch_id=${encodeURIComponent(branchId)}&status=PENDING_REVIEW&q=${encodeURIComponent(a.username || a.full_name || '')}`;

export default function ShiftPreferenceWeeklyGrid({
  branchId,
  cellParam,
  onSelectCell,
  applicantId,
  onSelectApplicant,
}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showApplicants, setShowApplicants] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!branchId) return undefined;
    let cancelled = false;
    setLoading(true);
    setError('');
    // Đổi chi nhánh: bỏ ngay số của chi nhánh cũ, không để lưới cũ đứng đó trong lúc tải.
    setData(null);
    shiftPreferenceAPI
      .getWeeklyGrid(branchId)
      .then((res) => {
        if (!cancelled) setData(res);
      })
      .catch((e) => {
        if (cancelled) return;
        // eslint-disable-next-line no-console
        console.error(e);
        setError(e?.message || 'Không tải được lưới tuần ca ưu tiên.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [branchId, reloadKey]);

  const templates = useMemo(() => data?.templates || [], [data]);
  const days = data?.days || [];
  const summary = data?.summary;

  const templateById = useMemo(() => {
    const m = new Map();
    templates.forEach((t) => m.set(t.id, t));
    return m;
  }, [templates]);

  // Ô đang chọn: lấy từ `?o=`; không có (hoặc không còn hợp lệ sau khi đổi chi nhánh) thì tự
  // mở ô thiếu nhất để khung chi tiết không trống.
  const selected = useMemo(() => {
    const fromUrl = parseCellParam(cellParam);
    const valid = (c) => {
      const t = c && templateById.get(c.templateId);
      return t && t.cells.some((x) => x.day === c.day && x.open) ? c : null;
    };
    const picked = valid(fromUrl);
    if (picked) return picked;
    const first = summary?.shortest?.[0];
    return first ? valid({ templateId: first.template_id, day: first.day }) : null;
  }, [cellParam, templateById, summary]);

  const selectedTemplate = selected ? templateById.get(selected.templateId) : null;
  const selectedCell = selectedTemplate?.cells.find((c) => c.day === selected.day) || null;
  const selectedDayLabel = days.find((d) => d.day === selected?.day)?.label || '';

  // #171710 (YC-123608 US-4): ứng viên đang xem — dựng từ `applicants` đã có trong JSON lưới
  // (không gọi API mới). Ứng viên không có ô nào thì không hiện trong danh sách.
  const applicants = useMemo(() => {
    const m = new Map();
    templates.forEach((t) =>
      t.cells.forEach((c) =>
        (c.applicants || []).forEach((a) => {
          if (!m.has(a.id)) m.set(a.id, a);
        }),
      ),
    );
    return Array.from(m.values()).sort((a, b) =>
      String(a.full_name || '').localeCompare(String(b.full_name || ''), 'vi'),
    );
  }, [templates]);
  const applicantSel = applicantId ? applicants.find((a) => a.id === applicantId) || null : null;
  const uvCellIds = (c) => !!applicantSel && (c.applicants || []).some((a) => a.id === applicantId);
  const uvDu = applicantSel
    ? templates.reduce(
        (n, t) => n + t.cells.filter((c) => c.level === 'plus2' && uvCellIds(c)).length,
        0,
      )
    : 0;

  const pick = (templateId, day) => onSelectCell(`${templateId}-${day}`);

  if (loading) {
    return (
      <div className="spwg-state" data-testid="spwg-loading">
        <Spinner animation="border" role="status" size="sm" className="me-2" />
        Đang tải lưới tuần…
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="danger" className="d-flex align-items-center justify-content-between gap-2" data-testid="spwg-error">
        <span>Không tải được lưới tuần ca ưu tiên: {error}</span>
        <Button variant="outline-danger" size="sm" onClick={() => setReloadKey((k) => k + 1)}>
          Thử lại
        </Button>
      </Alert>
    );
  }

  if (!data) return null;

  if (templates.length === 0) {
    return (
      <Alert variant="secondary" data-testid="spwg-empty">
        Chi nhánh này chưa cấu hình ca làm việc.
      </Alert>
    );
  }

  return (
    <div className="spwg" data-testid="spwg">
      <div className="spwg-card spwg-summary">
        <div className="spwg-summary-nums">
          <div className="spwg-num spwg-num-none">
            <b data-testid="spwg-none-count">{summary.none_count}</b>ô ca chưa ai đăng ký
          </div>
          <div className="spwg-num spwg-num-just">
            <b data-testid="spwg-just-count">{summary.just_count}</b>ô ca chỉ đủ người, không ai dự phòng
          </div>
        </div>
        {summary.shortest.length > 0 && (
          <>
            <div className="spwg-muted">Thiếu người nhất (lặp lại mỗi tuần):</div>
            <div className="spwg-chips">
              {summary.shortest.map((c) => (
                <button
                  type="button"
                  key={`${c.template_id}-${c.day}`}
                  className={`spwg-chip ${LEVEL_CLASS[c.level]}`}
                  data-testid="spwg-chip"
                  onClick={() => pick(c.template_id, c.day)}
                >
                  {shortCellLabel(c.day_label, templateById.get(c.template_id))}: <b>{c.count}</b>
                </button>
              ))}
            </div>
          </>
        )}
      </div>

      <label className="spwg-toggle">
        <Form.Check
          type="checkbox"
          id="spwg-show-applicants"
          checked={showApplicants}
          onChange={(e) => setShowApplicants(e.target.checked)}
          label="Hiện cả ứng viên chờ duyệt ở khung chi tiết ô"
        />
      </label>

      <div className="spwg-uv-chon">
        <Form.Select
          size="sm"
          aria-label="Xem ca của ứng viên"
          data-testid="spwg-ung-vien"
          value={applicantSel ? applicantId : ''}
          disabled={applicants.length === 0}
          onChange={(e) => onSelectApplicant?.(e.target.value)}
        >
          {applicants.length === 0 ? (
            <option value="" disabled>
              Không có ứng viên chờ duyệt
            </option>
          ) : (
            <>
              <option value="">— Không xem ứng viên —</option>
              {applicants.map((a) => (
                <option key={a.id} value={a.id}>
                  {`${a.full_name} · ${a.total_shifts_per_week} ca`}
                </option>
              ))}
            </>
          )}
        </Form.Select>
      </div>

      <div className="spwg-layout">
        <div>
          {applicantId && !applicantSel && (
            <Alert variant="secondary" className="spwg-uv-het" data-testid="spwg-uv-het">
              Ứng viên này không còn chờ duyệt ở chi nhánh đang xem.
            </Alert>
          )}
          {applicantSel && (
            <div className="spwg-card spwg-uv-tomtat" data-testid="spwg-uv-tomtat">
              <div>
                <b>{applicantSel.full_name}</b> chọn {applicantSel.total_shifts_per_week} ca/tuần ·
                lấp {applicantSel.short_cells_covered} ô đang thiếu · {uvDu} ô đã dư
              </div>
              <Button as={Link} to={hoSoLink(branchId, applicantSel)} variant="primary" size="sm">
                Mở hồ sơ
              </Button>
            </div>
          )}
          <div className="spwg-card spwg-table-wrap">
            <table className="spwg-table" data-testid="spwg-table">
              <thead>
                <tr>
                  <th aria-label="Ca" />
                  {days.map((d) => (
                    <th key={d.day}>{d.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {templates.map((t) => (
                  <tr key={t.id}>
                    <th scope="row">
                      <div className="spwg-name">
                        {t.name}
                        {t.is_cashier && <span className="spwg-tn" title="Ca thu ngân">TN</span>}
                      </div>
                      <div className="spwg-time">{formatTemplateTime(t)}</div>
                    </th>
                    {t.cells.map((c) => {
                      const dayLabel = days.find((d) => d.day === c.day)?.label || '';
                      if (!c.open) {
                        return (
                          <td
                            key={c.day}
                            className="spwg-closed"
                            title="Ca này không mở ngày này"
                            data-testid="spwg-cell-closed"
                          >
                            —
                          </td>
                        );
                      }
                      const isSel = selected && selected.templateId === t.id && selected.day === c.day;
                      // #171710: số trong ô KHÔNG cộng ứng viên — chỉ thêm ✓ + viền khi ứng viên chọn ô này.
                      const isUv = uvCellIds(c);
                      return (
                        <td key={c.day} className={`${LEVEL_CLASS[c.level]}${isSel ? ' spwg-sel' : ''}`}>
                          <button
                            type="button"
                            className={`spwg-cell-btn${isUv ? ' spwg-uv' : ''}`}
                            data-testid="spwg-cell"
                            data-o={`${t.id}-${c.day}`}
                            data-level={c.level}
                            data-ung-vien={isUv ? '1' : undefined}
                            aria-pressed={!!isSel}
                            aria-label={`${dayLabel} · ${t.name}: ${c.count} người${isUv ? ', ứng viên đã chọn' : ''}`}
                            onClick={() => pick(t.id, c.day)}
                          >
                            {isUv ? `${c.count} ✓` : c.count}
                          </button>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="spwg-legend">
            <span><i className="spwg-none" />Chưa ai</span>
            <span><i className="spwg-just" />Chỉ đủ người, không dự phòng</span>
            <span><i className="spwg-plus1" />Dư 1 người</span>
            <span><i className="spwg-plus2" />Dư từ 2 người</span>
            <span><i className="spwg-closed-dot" />Ca không mở ngày đó</span>
            <span><span className="spwg-tn">TN</span> ca thu ngân</span>
          </div>
        </div>

        {selectedTemplate && selectedCell && (
          <div className="spwg-card spwg-detail" data-testid="spwg-detail">
            <h3>
              {selectedDayLabel} · {selectedTemplate.name} ({formatTemplateTime(selectedTemplate)})
            </h3>
            <div className="spwg-muted">
              Cần {selectedTemplate.max_employees} người · đang có <b>{selectedCell.count}</b> nhân
              viên đăng ký ưu tiên
            </div>

            <div className="spwg-sub">Nhân viên đang làm đã đăng ký ô này</div>
            {selectedCell.employees.length === 0 ? (
              <div className="spwg-row spwg-muted">Chưa ai đăng ký ô này.</div>
            ) : (
              selectedCell.employees.map((e) => (
                <div className="spwg-row" key={e.id} data-testid="spwg-employee">
                  <span className="spwg-person">
                    {e.full_name}
                    {e.is_new_hire && (
                      <Badge bg="info" className="ms-1" title="Nhân viên còn đang tập việc">
                        Mới
                      </Badge>
                    )}
                  </span>
                </div>
              ))
            )}

            {showApplicants && (
              <>
                <div className="spwg-sub">Ứng viên chờ duyệt có đăng ký ô này</div>
                {selectedCell.applicants.length === 0 ? (
                  <div className="spwg-row spwg-muted">Chưa có ứng viên chờ duyệt đăng ký ô này.</div>
                ) : (
                  selectedCell.applicants.map((a) => (
                    <div className="spwg-row" key={a.id} data-testid="spwg-applicant">
                      <div>
                        <div className="spwg-person">
                          {a.full_name}
                          <span className="spwg-badge-wait">Chờ duyệt</span>
                        </div>
                        <div className="spwg-muted">
                          Đăng ký {a.total_shifts_per_week} ca/tuần · lấp được {a.short_cells_covered} ô
                          đang thiếu
                        </div>
                      </div>
                      <Button
                        as={Link}
                        to={hoSoLink(branchId, a)}
                        variant="primary"
                        className="spwg-open-btn"
                      >
                        Mở hồ sơ
                      </Button>
                    </div>
                  ))
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
